/**
 * Shared derivation of the overall compliance status badge (TASK-555 / review M1).
 *
 * The bug this fixes: the two duplicated IIFEs in CompliancePage and ComplianceStatusPanel
 * rendered green "Compliant" whenever the score was non-null AND there were no alerts AND no
 * pending reviews — the score VALUE was ignored, so a low score (e.g. 43%) with an empty queue
 * still read green. This helper is the single source of truth and thresholds on the score,
 * mirroring the existing compliance-domain convention (>=80 green / 60-79 amber / <60 red;
 * see ComplianceScore.tsx and types/documentCompliance.ts).
 */
import { CheckCircle, AlertTriangle, Clock, ShieldAlert, type LucideIcon } from 'lucide-react';

export interface ComplianceOverall {
  label: string;
  text: string; // text color class
  icon: string; // icon color class
  Icon: LucideIcon;
}

interface SummaryLike {
  compliance_score?: number | null;
  risk_alerts?: number | null;
  pending_reviews?: number | null;
}

const GREEN_THRESHOLD = 80;
const AMBER_THRESHOLD = 60;

export function deriveComplianceOverall(
  data: SummaryLike | null | undefined,
  opts: { loading?: boolean; error?: boolean } = {},
): ComplianceOverall {
  if (opts.loading)
    return { label: '…', text: 'text-gray-400', icon: 'text-gray-300', Icon: Clock };
  if (opts.error || !data || data.compliance_score === null || data.compliance_score === undefined)
    return { label: 'No data', text: 'text-gray-500', icon: 'text-gray-400', Icon: Clock };
  if ((data.risk_alerts ?? 0) > 0)
    return { label: 'Attention', text: 'text-red-600', icon: 'text-red-500', Icon: AlertTriangle };
  if ((data.pending_reviews ?? 0) > 0)
    return { label: 'Reviews pending', text: 'text-amber-600', icon: 'text-amber-500', Icon: Clock };
  // Queue is empty — now the score value decides. A low score is NOT "Compliant".
  const score = data.compliance_score;
  if (score < AMBER_THRESHOLD)
    return { label: 'Non-compliant', text: 'text-red-600', icon: 'text-red-500', Icon: ShieldAlert };
  if (score < GREEN_THRESHOLD)
    return { label: 'Needs attention', text: 'text-amber-600', icon: 'text-amber-500', Icon: AlertTriangle };
  return { label: 'Compliant', text: 'text-green-600', icon: 'text-green-500', Icon: CheckCircle };
}
